import React, { useState, useEffect } from 'react';
import { Container, Card, CardBody, CardHeader, Form, Button, Alert, Row, Col, FloatingLabel } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import PageTitle from '../components/PageTitle';
import { resolveConferenceTypeDisplay, toTransparentBadgeStyle } from '../constants/conferenceTypes';

const PublicAttendancePage = () => {
  const [session, setSession] = useState(null);
  const [conference, setConference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [attendanceMethod, setAttendanceMethod] = useState(''); // 'login' or 'manual'
  const [currentStep, setCurrentStep] = useState(1);
  const [attendanceCode, setAttendanceCode] = useState('');
  const [manualData, setManualData] = useState({
    firstName: '',
    lastName: '',
    role: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [copiedZoom, setCopiedZoom] = useState(false);

  // Get masked attendance code from URL
  // Expected pattern: .../masked-attendance/{maskedCode}/public
  // Also supports fallback query param ?code=XYZ
  const getMaskedCodeFromLocation = () => {
    try {
      const url = new URL(window.location.href);
      const queryCode = url.searchParams.get('code');
      if (queryCode) return decodeURIComponent(queryCode).trim();

      const segments = window.location.pathname.split('/').filter(Boolean);
      if (segments.length === 0) return '';

      const last = segments[segments.length - 1];
      // If the URL ends with "/public", the code is the previous segment
      if (last.toLowerCase() === 'public') {
        const prev = segments.length >= 2 ? segments[segments.length - 2] : '';
        return decodeURIComponent(prev || '').trim();
      }

      // Otherwise, use the last segment (e.g., /attendance/{code})
      return decodeURIComponent(last || '').trim();
    } catch (e) {
      return '';
    }
  };

  const maskedCodeParam = getMaskedCodeFromLocation();

  useEffect(() => {
    if (!maskedCodeParam) {
      setLoading(false);
      setError('Invalid or missing attendance code. Please scan a valid QR code or use the provided link.');
      return;
    }
    fetchSessionData();
  }, [maskedCodeParam]);

  // Sync local isDarkMode with global theme setting
  useEffect(() => {
    const checkDarkMode = () => {
      const storedTheme = localStorage.getItem("theme");
      const bodyTheme = document.body.getAttribute('data-bs-theme');
      const dark = storedTheme ? storedTheme === 'dark' : bodyTheme === 'dark';
      setIsDarkMode(!!dark);
    };

    checkDarkMode();

    const handleStorageChange = (e) => {
      if (e.key === 'theme') {
        checkDarkMode();
      }
    };

    const observer = new MutationObserver(() => {
      checkDarkMode();
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-bs-theme'] });

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      observer.disconnect();
    };
  }, []);

  const fetchSessionData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(`${API_BASE_URL}/api/conference-sessions/masked-attendance/${maskedCodeParam}/public`);
      
      if (response.data.session) {
        setSession(response.data.session);
        setConference(response.data.conference);
        setAttendanceCode(response.data.session.attendanceCode || '');
      } else {
        setError('Session not found or not available for public attendance');
      }
    } catch (error) {
      console.error('Error fetching session data:', error);
      const backendMessage = error.response?.data?.error || error.response?.data?.message;
      setError(backendMessage || 'Failed to load session information. Please check the QR code or try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginAttendance = async () => {
    try {
      setSubmitting(true);
      setError('');
      
      const response = await axios.post(`${API_BASE_URL}/api/conference-attendance/public/login`, {
        attendanceCode: attendanceCode.trim().toUpperCase()
      });
      
      // Redirect to login page with redirect parameter
      if (response.data.loginUrl) {
        window.location.href = response.data.loginUrl;
      } else {
        setError('Failed to redirect to login page');
      }
    } catch (error) {
      console.error('Error processing login attendance:', error);
      setError(error.response?.data?.error || 'Failed to process login request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualAttendance = async () => {
    try {
      setSubmitting(true);
      setError('');
      
      if (!manualData.firstName.trim() || !manualData.lastName.trim() || !manualData.role.trim()) {
        setError('Please fill in all required fields.');
        return;
      }
      
      const response = await axios.post(`${API_BASE_URL}/api/conference-attendance/public/manual`, {
        attendanceCode: attendanceCode.trim().toUpperCase(),
        firstName: manualData.firstName.trim(),
        lastName: manualData.lastName.trim(),
        role: manualData.role.trim()
      });
      
      setSuccess('Attendance recorded successfully! You can now close this tab.');
      setTimeout(() => {
        // Try to close the tab, but provide fallback if it doesn't work
        try {
          window.close();
        } catch (e) {
          // If window.close() fails, show a message to manually close
          setSuccess('Attendance recorded successfully! Please close this tab manually.');
        }
      }, 2000);
    } catch (error) {
      console.error('Error recording attendance:', error);
      setError(error.response?.data?.error || 'Failed to record attendance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const minute = parseInt(minutes);
      
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const displayMinute = minute.toString().padStart(2, '0');
      
      return `${displayHour}:${displayMinute} ${period}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  const renderConferenceType = (type) => {
    const typeOptions = Array.isArray(conference?.creator?.conferenceTypePreferences)
      ? conference.creator.conferenceTypePreferences
      : undefined;
    const resolved = resolveConferenceTypeDisplay(type, typeOptions);
    return {
      label: resolved.label || type,
      name: resolved.name || type,
      color: resolved.color,
    };
  };

  const zoomLink = conference?.zoomLink || session?.zoomLink;

  const handleCopyZoomLink = async () => {
    if (!zoomLink) return;
    try {
      await navigator.clipboard.writeText(zoomLink);
      setCopiedZoom(true);
      setTimeout(() => setCopiedZoom(false), 2000);
    } catch (err) {
      console.error('Failed to copy Zoom link:', err);
      const textArea = document.createElement('textarea');
      textArea.value = zoomLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedZoom(true);
      setTimeout(() => setCopiedZoom(false), 2000);
    }
  };

  if (loading) {
    return (
      <div>
        <PageTitle title="Loading..." />
        <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
          <Container>
            <div className="text-center">
              <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <h5 className="text-dark mb-2">Loading Conference Information</h5>
              <p className="text-muted">Please wait while we fetch the details...</p>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  if (error && !session) {
    return (
      <div>
        <PageTitle title="Attendance Error" />
        <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
          <Container>
            <Card className="shadow-lg border-0" style={{ borderRadius: '16px' }}>
              <CardBody className="text-center p-5">
                <div className="d-inline-flex align-items-center justify-content-center bg-danger bg-gradient rounded-circle mb-4" 
                     style={{ width: '80px', height: '80px' }}>
                  <i className="bi bi-exclamation-triangle text-white" style={{ fontSize: '2rem' }}></i>
                </div>
                <h4 className="text-danger mb-3">Error</h4>
                <p className="text-muted mb-4">{error}</p>
              </CardBody>
            </Card>
          </Container>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div>
        <PageTitle title="Attendance Success" />
        <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
          <Container>
            <Card className="shadow-lg border-0" style={{ borderRadius: '16px' }}>
              <CardBody className="text-center p-5">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4" 
                     style={{ width: '80px', height: '80px', backgroundColor: 'var(--bs-green)'}}>
                  <i className="bi bi-check-circle text-white" style={{ fontSize: '2rem' }}></i>
                </div>
                <h4 className="text-success mb-3">Success!</h4>
                <p className="text-muted mb-4">{success}</p>
                <p className="text-muted mb-4">You can now close this tab.</p>
              </CardBody>
            </Card>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Container>
        <Row className="justify-content-center">
          <Col lg={8} className=" py-3 border-bottom">
            <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
            <img src={isDarkMode ? "/images/asset4.svg" : "/images/asset5.svg"} alt="brand logo" style={{ height: "16px", borderRight: "2px solid var(--gray-400)", paddingRight: "10px"}}/>
                <p className="mb-0 fw-semibold text-muted">Conference Attendance</p>
                </div>
                <Button variant="light-solid" size="sm" onClick={() => {
                  window.location.href = "/";
                }}>
                  Home
                  <i className="bi bi-arrow-right ms-2"></i>
                </Button>
            </div>
          </Col>
        </Row>
      </Container>
      <PageTitle title="Conference Attendance" description="Record your attendance for conference sessions" />
      <Container className="d-flex align-items-center justify-content-center" style={{minHeight: 'calc(100vh - 72px)'}}>
        <Row className="w-100 justify-content-center">
          <Col md={8} lg={8}>
            <Card className="shadow-sm border-0">
              <CardBody className="p-md-4">
                {conference && session && (
                  <>
                    {/* Conference Details - aligned with PublicQRCodePage design */}
                    <div className="mb-4">

                      <Row className="g-4 align-items-stretch">
                        <Col lg={12}>
                          <div className="mb-3 d-flex flex-row align-items-center justify-content-between">
                            <h4 className="fw-bold mb-0">{conference.title}</h4>  
                            <span
                              className="badge rounded-pill px-3 py-2"
                              style={toTransparentBadgeStyle(renderConferenceType(conference.type).color)}
                              title={renderConferenceType(conference.type).name}
                            >
                              {renderConferenceType(conference.type).label}
                            </span>
                          </div>
                        </Col>
                        <Col lg={ zoomLink ? 8 : 12}>
                          <Row className="g-3">
                            {session.speaker && (
                              <Col md={7}>
                                <div className="text-start d-flex align-items-center px-3 gap-2">
                                  <span className="d-inline-flex align-items-center justify-content-center rounded-3 bg-primary bg-opacity-10" style={{ width: 40, height: 40 }}>
                                    <i className="bi bi-person-fill text-primary"></i>
                                  </span>
                                  <div className="d-flex flex-column align-items-start justify-content-start">
                                    <div className="text-muted text-uppercase small fw-semibold">Speaker</div>
                                    <div className="fw-medium">
                                      {session.speaker}
                                      {session.speakerTitle && ` (${session.speakerTitle})`}
                                    </div>
                                  </div>
                                </div>
                              </Col>
                            )}

                            {session.location && (
                              <Col md={5}>
                                <div className="text-start d-flex align-items-center px-3 gap-2">
                                  <span className="d-inline-flex align-items-center justify-content-center rounded-3 bg-danger bg-opacity-10" style={{ width: 40, height: 40 }}>
                                    <i className="bi bi-geo-alt text-danger"></i>
                                  </span>
                                  <div className="d-flex flex-column align-items-start justify-content-start">
                                    <div className="text-muted text-uppercase small fw-semibold">Location</div>
                                    <div className="fw-medium">{session.location}</div>
                                  </div>
                                </div>
                              </Col>
                            )}

                            <Col md={7}>
                              <div className="d-flex align-items-start gap-2 p-3 rounded-3 bg-light">
                                <div className="d-inline-flex align-items-center justify-content-center rounded-3 bg-cyan bg-opacity-10" style={{ width: 40, height: 40 }}>
                                  <i className="bi bi-calendar-event text-cyan"></i>
                                </div>
                                <div className="text-start">
                                  <div className="text-muted text-uppercase small fw-semibold">Date</div>
                                  <div className="fw-semibold">
                                    {new Date(session.date + 'T00:00:00').toLocaleDateString('en-US', { 
                                      weekday: 'long', 
                                      year: 'numeric', 
                                      month: 'long', 
                                      day: 'numeric' 
                                    })}
                                  </div>
                                </div>
                              </div>
                            </Col>

                            {conference.startTime && conference.endTime && (
                              <Col md={5}>
                                <div className="d-flex align-items-start gap-3 p-3 rounded-3 bg-light">
                                  <div className="d-inline-flex align-items-center justify-content-center rounded-3 bg-info bg-opacity-10" style={{ width: 40, height: 40 }}>
                                    <i className="bi bi-clock text-info"></i>
                                  </div>
                                  <div className="text-start">
                                    <div className="text-muted text-uppercase small fw-semibold">Time</div>
                                    <div className="fw-semibold">
                                      {formatTime(conference.startTime)} - {formatTime(conference.endTime)}
                                    </div>
                                  </div>
                                </div>
                              </Col>
                            )}

                            {/*<Col md={12}>
                              <div className="d-flex align-items-center mb-2 mt-1">
                                <i className="bi bi-key text-muted me-2"></i>
                                <small className="text-muted fw-semibold">Attendance Code</small>
                              </div>
                              <div className="bg-light p-2 rounded" style={{ borderRadius: '8px' }}>
                                <div className="fs-4 card-bg-container px-2 py-1 rounded">
                                  <h4 className="fw-bold p-3 mb-0 text-main-gradient">
                                    {session.attendanceCode}
                                  </h4>
                                </div>
                              </div>
                            </Col>*/}
                          </Row>
                        </Col>

                        {zoomLink && (
                          <Col lg={ zoomLink ? 4 : 0}>
                            <div
                              className="d-flex flex-column justify-content-between rounded-3 p-4"
                              style={{
                                background: 'linear-gradient(135deg, var(--blue-light) 0%, var(--blue-lighter) 100%)',
                                border: '1px solid rgba(59,130,246,0.15)'
                              }}
                            >
                              <div className="text-start mb-3">
                                <div className="text-muted text-uppercase small fw-semibold">Virtual Meeting</div>
                                <h6 className="fw-semibold mb-0">Zoom Conference</h6>
                              </div>
                              <div className="d-grid gap-2">
                                <Button
                                  variant="primary"
                                  size="sm"
                                  href={zoomLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="fw-semibold py-2"
                                >
                                  <i className="bi bi-camera-video me-2"></i>
                                  Join Zoom Meeting
                                </Button>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={handleCopyZoomLink}
                                  className="fw-semibold py-2"
                                >
                                  <i className={`bi ${copiedZoom ? 'bi-check-lg text-success' : 'bi-link-45deg'} me-2`}></i>
                                  {copiedZoom ? 'Copied Zoom Link' : 'Copy Zoom Link'}
                                </Button>
                              </div>
                            </div>
                          </Col>
                        )}
                      </Row>

                      {session.description && (
                        <Row className="mt-3">
                          <Col>
                            <div className="mb-0 text-start">
                              <label className="form-label text-muted small fw-semibold mb-1">Description</label>
                              <p className="mb-0 fw-medium">{session.description}</p>
                            </div>
                          </Col>
                        </Row>
                      )}

                      {/*<Row className="mt-4">
                        <Col>
                          <div
                            className="p-4 rounded-3 text-start"
                            style={{
                              background: 'rgba(59,130,246,0.06)',
                              border: '1px solid rgba(59,130,246,0.15)'
                            }}
                          >
                            <div className="d-flex align-items-start gap-3">
                              <div className="d-inline-flex align-items-center justify-content-center rounded-3 bg-primary bg-opacity-10" style={{ width: 40, height: 40 }}>
                                <i className="bi bi-info-circle text-primary"></i>
                              </div>
                              <div>
                                <h6 className="fw-semibold mb-2">How to Check In</h6>
                                <ul className="mb-0 small text-muted">
                                  <li>If you arrived via QR code, you can proceed below to record attendance.</li>
                                  <li>Alternatively, use the attendance code above on supported systems.</li>
                                  <li>Check-in is only available around the scheduled conference time.</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>*/}
                    </div>

                    {/* Step 1: Method Selection Card */}
                    {currentStep === 1 && (
                      <Card className="bg-light border-0 rounded-3">
                        <CardBody className="p-md-4">
                          <h6 className="fw-bold mb-3">Choose Method to Record Attendance</h6>
                          
                          <div className="d-flex flex-column flex-md-row gap-2 w-100">
                            <Button
                              variant={attendanceMethod === 'login' ? 'primary' : 'outline-alt'}
                              onClick={() => {
                                setAttendanceMethod('login');
                                setCurrentStep(2);
                              }}
                              className="text-start p-3 w-100"
                             
                            >
                              <div className="d-flex align-items-center">
                                <div>
                                  <span className="d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded-circle me-3" style={{ width: '40px', height: '40px' }}>
                                    <i className="bi bi-person-check" style={{ fontSize: '1.2rem' }}></i>
                                  </span>
                                </div>
                                <div>
                                  <div className="fw-bold">Login</div>
                                  <div className="text-muted small">Use your account</div>
                                </div>
                              </div>
                            </Button>

                            <Button
                              variant={attendanceMethod === 'manual' ? 'primary' : 'outline-alt'}
                              onClick={() => {
                                setAttendanceMethod('manual');
                                setCurrentStep(2);
                              }}
                              className="text-start p-3 w-100"
                            >
                              <div className="d-flex align-items-center">
                                <div>
                                  <span className="d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded-circle me-3" style={{ width: '40px', height: '40px' }}>
                                    <i className="bi bi-person-plus" style={{ fontSize: '1.2rem' }}></i>
                                  </span>
                                </div>
                                <div>
                                  <div className="fw-bold">Manual Entry</div>
                                  <div className="text-muted small">Enter credentials</div>
                                </div>
                              </div>
                            </Button>
                          </div>
                        </CardBody>
                      </Card>
                    )}



                    {/* Step 2: Login Method Card */}
                    {currentStep === 2 && attendanceMethod === 'login' && (
                      <Card className="bg-light border-0 rounded-3">
                        <CardBody className="p-md-4">
                          <h6 className="fw-bold mb-3">Login to Record Attendance</h6>
                          <p className="text-muted mb-4">
                            You'll be redirected to the login page. After logging in, you'll be automatically redirected back to record your attendance.
                          </p>
                          <Button
                            variant="primary"
                            onClick={handleLoginAttendance}
                            disabled={submitting}
                            className="w-100 py-3"
                            style={{ borderRadius: '12px' }}
                          >
                            {submitting ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Processing...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-box-arrow-in-right me-2"></i>
                                Login and Record Attendance
                              </>
                            )}
                                                     </Button>
                           
                         </CardBody>
                       </Card>
                     )}

                     {/* Back Button Outside Card */}
                     {currentStep === 2 && attendanceMethod === 'login' && (
                       <div className="mt-3">
                         <Button
                           variant="light"
                           size="sm"
                           onClick={() => {
                             setCurrentStep(1);
                             setAttendanceMethod('');
                           }}
                         >
                           <i className="bi bi-chevron-left me-2"></i>
                           Back
                         </Button>
                       </div>
                     )}

                    {/* Step 2: Manual Method Card */}
                    {currentStep === 2 && attendanceMethod === 'manual' && (
                      <Card className="bg-light border-0 rounded-3">
                        <CardBody className="p-md-4">
                          <h6 className="fw-bold mb-3">Manual Attendance Entry</h6>
                          <Form>
                            <Row>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <FloatingLabel controlId="floatingInput" label="First Name" className="mb-3 required-field">
                                  <Form.Control
                                    type="text"
                                    value={manualData.firstName}
                                    onChange={(e) => setManualData(prev => ({ ...prev, firstName: e.target.value }))}
                                    placeholder="Enter your first name"
                                    required
                                  />
                                  </FloatingLabel>
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <FloatingLabel controlId="floatingInput" label="Last Name" className="mb-3 required-field">
                                  <Form.Control
                                    type="text"
                                    value={manualData.lastName}
                                    onChange={(e) => setManualData(prev => ({ ...prev, lastName: e.target.value }))}
                                    placeholder="Enter your last name"
                                    required
                                  />
                                  </FloatingLabel>
                                </Form.Group>
                              </Col>
                            </Row>
                            <Form.Group className="mb-4">
                              <FloatingLabel controlId="floatingSelect" label="Role" className="mb-3 required-field">
                              <Form.Select
                                value={manualData.role}
                                onChange={(e) => setManualData(prev => ({ ...prev, role: e.target.value }))}
                                required
                                style={{ borderRadius: '8px' }}
                              >
                                <option value="">Select your role</option>
                                <option value="resident">Resident</option>
                                <option value="attending">Attending</option>
                                <option value="medical_student">Medical Student</option>
                                <option value="director">Director</option>
                              </Form.Select>
                              </FloatingLabel>
                            </Form.Group>
                            <Button
                              variant="primary"
                              onClick={handleManualAttendance}
                              disabled={submitting || !manualData.firstName || !manualData.lastName || !manualData.role}
                              className="w-100 py-3"
                              style={{ borderRadius: '12px' }}
                            >
                              {submitting ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Recording Attendance...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-check-circle me-2"></i>
                                  Record Attendance
                                </>
                                                             )}
                             </Button>
                             
                           </Form>
                         </CardBody>
                       </Card>
                     )}

                     {/* Back Button Outside Card */}
                     {currentStep === 2 && attendanceMethod === 'manual' && (
                       <div className="mt-3">
                         <Button
                           variant="light"
                           size="sm"
                           onClick={() => {
                             setCurrentStep(1);
                             setAttendanceMethod('');
                           }}
                         >
                           <i className="bi bi-chevron-left me-2"></i>
                           Back
                         </Button>
                       </div>
                     )}

                    {error && (
                      <Alert variant="danger" className="mt-3 border-0" style={{ borderRadius: '12px' }}>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {error}
                      </Alert>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PublicAttendancePage; 